import { HashLink } from "@/components/HashLink";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { useT, useLang } from "@/lib/i18n";
import { getToken } from "@/lib/utils/auth-storage";
import { isSSOAsync, navigateToToolbox } from "@/lib/utils/sso";
import { useState } from "react";

// Product images
import dataImg from "@/assets/products/core-data.png";
import memoryImg from "@/assets/products/core-memory.png";
import modelImg from "@/assets/products/core-model.png";
import genImg from "@/assets/products/gen.png";
import hubImg from "@/assets/products/hub.png";
import medImg from "@/assets/products/med.png";
import simImg from "@/assets/products/sim.png";

type ProductSection = {
  id: string;
  pillEn: string;
  pillZh: string;
  titleEn: string;
  titleZh: string;
  descEn: string;
  descZh: string;
  tagsEn: string[];
  tagsZh: string[];
  captionEn: string;
  captionZh: string;
  image: string;
  reverse: boolean;
};

type CoreCard = {
  numEn: string;
  numZh: string;
  nameEn: string;
  nameZh: string;
  descEn: string;
  descZh: string;
  image: string;
};

interface ProductsPageProps {
  /** 请求弹出登录弹窗，传入 'toolbox' 表示登录后跳转 toolbox */
  onRequestLogin?: (redirect?: "toolbox") => void;
}

const ProductsPage: React.FC<ProductsPageProps> = ({ onRequestLogin }) => {
  const t = useT();
  const { lang } = useLang();
  const [formOpen, setFormOpen] = useState(false);
  const [isNavigatingToToolbox, setIsNavigatingToToolbox] = useState(false);

  const handleTryNow = async () => {
    const token = getToken();
    if (!token) {
      onRequestLogin?.("toolbox");
      return;
    }
    if (isSSOAsync()) {
      setIsNavigatingToToolbox(true);
      try {
        await navigateToToolbox();
      } catch {
        // navigateToToolbox 内部已有降级逻辑
      } finally {
        setIsNavigatingToToolbox(false);
      }
    } else {
      navigateToToolbox();
    }
  };

  // Helper function to highlight keywords in title
  const renderTitle = (en: string, zh: string, keywords: { en: string; zh: string }[]) => {
    let enResult = en;
    let zhResult = zh;

    keywords.forEach(({ en: enKeyword, zh: zhKeyword }) => {
      const enPattern = new RegExp(`(${enKeyword})`, 'gi');
      const zhPattern = new RegExp(`(${zhKeyword})`, 'g');

      if (enPattern.test(en)) {
        enResult = enResult.replace(enPattern, '<span class="brand-text">$1</span>');
      }
      if (zhPattern.test(zh)) {
        zhResult = zhResult.replace(zhPattern, '<span class="brand-text">$1</span>');
      }
    });

    return {
      __html: t(enResult, zhResult)
    };
  };

  const modules = [
    { num: "01", en: "OranHub", cn: "Market Insight", zhCn: "市场洞察" },
    { num: "02", en: "OranSim", cn: "Predictive Simulation", zhCn: "预测预演" },
    { num: "03", en: "OranGen", cn: "Content Factory", zhCn: "内容工厂" },
    { num: "04", en: "OranMed", cn: "Distributed Media", zhCn: "分布式媒介" },
  ];

  const products: ProductSection[] = [
    {
      id: "oranhub",
      pillEn: "OranHub · Market Insight",
      pillZh: "OranHub · 市场洞察",
      titleEn: "Turn fragmented market signals into structured brand insights.",
      titleZh: "将碎片化市场信号，转化为结构化品牌洞察。",
      descEn:
        "OranHub pulls together scattered signals — social media posts, user feedback, brand share of voice, market trends — and turns them into clear brand diagnostics. Teams can quickly see where the brand stands today and where the next growth opportunity lies.",
      descZh:
        "OranHub 吸收来自社媒内容、用户反馈、品牌声量与市场趋势的碎片化信息，并将其转化为可判断、可行动的品牌洞察，帮助团队判断品牌当前的市场竞争地位，以及下一步增长机会在哪里。",
      tagsEn: ["Market Signals", "Brand Health", "Strategy Output"],
      tagsZh: ["市场信号", "品牌健康度", "策略输出"],
      captionEn:
        "It is not a traditional data dashboard, but a signal sensing and diagnostic layer before a brand enters global markets. This screen focuses on extracting actionable and decision-ready brand insights from complex, scattered, and unreadable market signals.",
      captionZh:
        "它不是传统数据看板，而是品牌进入全球市场前的信号感知与诊断层。从复杂、分散、不可读的市场信号中，提取出可判断、可行动的品牌洞察。",
      image: hubImg,
      reverse: false,
    },
    {
      id: "oransim",
      pillEn: "OranSim · Predictive Simulation",
      pillZh: "OranSim · 预测预演",
      titleEn: "Predict real ad performance before spending a single dollar.",
      titleZh: "在花出一分钱之前，预测广告投放的真实反馈。",
      descEn:
        "OranSim runs trials using virtual consumer groups and scenario simulations — trying out different selling points, content angles, and creator mixes before you commit. It turns marketing guesswork into clearer media buying decisions.",
      descZh:
        "OranSim 通过虚拟消费者群体与场景模拟，提前测试不同卖点、内容方向与达人组合的潜在表现，将营销不确定性转化为更清晰的投放判断，帮助品牌减少试错成本。",
      tagsEn: ["Star Map Simulation", "Scenario Testing", "KOL Matrix"],
      tagsZh: ["星图模拟", "场景测试", "KOL 矩阵"],
      captionEn:
        "Before media buying actually happens, OranSim uses simulation systems to predict market feedback on content, selling points, and creator combinations. It plays the role of prediction and decision support, helping brands reduce trial-and-error costs.",
      captionZh:
        "在投放真正发生前，先通过模拟系统预判内容、卖点与达人组合的市场反馈。它承担预测与决策角色，帮助品牌减少试错成本。",
      image: simImg,
      reverse: true,
    },
    {
      id: "orangen",
      pillEn: "OranGen · Content Factory",
      pillZh: "OranGen · 内容工厂",
      titleEn: "Turn strategic directions into scalable creative content.",
      titleZh: "将策略方向转化为可规模化生产的创意内容。",
      descEn:
        "Based on high-potential selling-point directions predicted by OranSim, OranGen uses multi-agent collaboration to perform trend matching, script generation, and video production, generating content assets in batches for different platforms, languages, and visual styles.",
      descZh:
        "OranGen 基于 OranSim 预测出的高潜力卖点方向，通过多智能体接力完成趋势匹配、脚本生成与视频产出，批量生成适配不同平台、语言与视觉风格的内容资产。  ",
      tagsEn: ["Strategic Core", "Creative Variants", "Batch Output"],
      tagsZh: ["策略核心", "创意变体", "批量输出"],
      captionEn:
        "It is not a single-point generation tool. Starting from the best-performing selling point direction, it turns strategy into scalable content assets through a continuous chain of trend understanding, script building, and video generation.",
      captionZh:
        "它不是单点生成工具，而是从最优卖点方向出发，通过趋势理解、脚本构建与视频生成的连续链路，将策略转化为可批量生产的内容资产。",
      image: genImg,
      reverse: false,
    },
    {
      id: "oranmed",
      pillEn: "OranMed · Distributed Media",
      pillZh: "OranMed · 分布式媒介",
      titleEn: "Bring creative content into real media distribution scenarios.",
      titleZh: "将创意内容送入真实的媒体传播场景。",
      descEn:
        "OranMed connects brand‑owned accounts, creator networks, AI search entry points, and e‑commerce touchpoints into one seamless flow. It handles content distribution, gathers market feedback, and supports conversion paths — helping content move from creation to real audience reach.",
      descZh:
        "OranMed 连接品牌授权账号矩阵、达人网络、AI 搜索入口与电商转化触点，完成内容分发、市场反馈回收与转化链路承接，形成可反馈、可优化的传播闭环，让内容从生成走向真实触达。",
      tagsEn: ["Account Matrix", "Social Media Scenarios", "Feedback Loop"],
      tagsZh: ["账号矩阵", "社媒场景", "反馈闭环"],
      captionEn:
        "After content is generated, brands still need to enter real communication scenarios. OranMed distributes content across account matrices, creator networks, AI search, and e-commerce touchpoints, forming a feedback-driven and optimizable distribution loop.",
      captionZh:
        "内容生成之后，品牌还需要进入真实传播场景。OranMed 将内容分发到账号矩阵、达人网络、AI 搜索与电商触点中，并形成可反馈、可优化的传播闭环。",
      image: medImg,
      reverse: true,
    },
  ];

  const coreCards: CoreCard[] = [
    {
      numEn: "01",
      numZh: "01",
      nameEn: "Memory Platform",
      nameZh: "记忆中台",
      descEn:
        "Centrally manages brand context, ensuring brand memory consistency across all stages of the task chain.",
      descZh:
        "统一管理品牌上下文，让品牌记忆在全链路任务中保持一致。",
      image: memoryImg,
    },
    {
      numEn: "02",
      numZh: "02",
      nameEn: "Data Platform",
      nameZh: "数据中台",
      descEn:
        "Aggregates multi-platform data from social media, e-commerce, and search to enable continuous data feedback.",
      descZh:
        "聚合社媒、电商、搜索多平台数据，让信号持续回流、可被复用。",
      image: dataImg,
    },
    {
      numEn: "03",
      numZh: "03",
      nameEn: "Model Platform",
      nameZh: "模型中台",
      descEn:
        "Automatically orchestrates appropriate model capabilities for different tasks, supporting the complete chain from insight to generation.",
      descZh:
        "为不同任务自动调度合适的模型能力，支撑从洞察到生成的完整链路。",
      image: modelImg,
    },
  ];

  return (
    <div className="landing-v2 min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section
        id="top"
        className="relative h-screen overflow-hidden bg-background pt-20"
      >
        <div className="relative h-full flex items-center justify-center">
          <div className="relative z-10 text-center px-6 max-w-[min(1400px,90vw)] mx-auto">
              {/* Platform badge */}
              <div className="inline-flex items-center gap-2 glass px-4 py-1.5 text-xs text-muted-foreground mb-8">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))] animate-pulse-glow" />
                {t("AI Marketing Platform", "AI 营销平台")}
              </div>

              {/* Main title */}
              <h1 className="mx-auto max-w-6xl text-6xl font-semibold tracking-tight leading-[1.05] md:text-7xl">
                {t(
                  <>
                    <span className="text-5xl md:text-7xl">From understanding the market</span>
                    <br />
                    to <span className="italic brand-text text-5xl md:text-7xl">being seen by users</span>
                  </>,
                  <span className="flex flex-col items-center justify-center">
                    <span>从看清市场</span>
                    <span>到<span className="italic brand-text">被用户看见</span></span>
                  </span>,
                )}
              </h1>

              {/* Subtitle */}
              <p className={`mx-auto max-w-3xl text-base md:text-lg text-muted-foreground leading-relaxed ${lang !== "zh" ? "mt-6" : "mt-10"}`}>
                {t(
                  "Oran AI connects the key stages of global brand growth through five modules: insight-driven decision-making, predictive engine, content factory, distributed media, and AI search visibility, helping brands move from market judgment to real audience reach.",
                  "OranAI 通过洞察决策、预测引擎、内容工厂、分布式媒介与 AI 搜索可见性五个模块，连接品牌出海增长中的关键环节，帮助品牌从市场判断走向真实触达。",
                )}
              </p>

              {/* Module navigation */}
              <nav className={`${lang !== "zh" ? "mt-12" : "mt-20"} w-full overflow-x-auto`}>
                <div className="mx-auto min-w-[1200px] grid grid-cols-4 gap-px overflow-hidden rounded-xl glass nebula-border">
                  {modules.map((m) => (
                    <HashLink
                      key={m.en}
                      to={`/products#${m.en.toLowerCase()}`}
                      className="group flex flex-col gap-3 bg-background/50 p-6 transition-colors hover:bg-background"
                    >
                      <span className="text-xs text-muted-foreground transition-colors group-hover:brand-text">
                        {m.num}
                      </span>
                      <span className="text-2xl font-medium">{m.en}</span>
                      <span className="text-sm text-muted-foreground">
                        {t(m.cn, m.zhCn)}
                      </span>
                    </HashLink>
                  ))}
                </div>
              </nav>

              {/* CTA Button */}
              <div className={`mx-auto ${lang !== "zh" ? "mt-10" : "mt-16"}`}>
                <button
                  onClick={handleTryNow}
                  disabled={isNavigatingToToolbox}
                  className="inline-flex items-center justify-center rounded-full px-12 py-2 text-lg font-medium text-white transition-colors hover:opacity-90 disabled:opacity-70"
                  style={{ background: "hsl(var(--brand))" }}
                >
                  {isNavigatingToToolbox
                    ? t("Redirecting\u2026", "跳转中\u2026")
                    : t("Try now", "立即体验")
                  }
                </button>
              </div>
            </div>
          </div>
      </section>

      {/* Product Sections */}
      {products.map((product, idx) => (
        <section
          key={product.id}
          id={product.id}
          className={`relative border-t border-border px-6 py-32 ${idx % 2 === 0 ? "bg-background" : "bg-background/50"}`}
        >
          <div
            className={`mx-auto max-w-[min(1600px,95vw)] grid lg:grid-cols-2 gap-16 items-center ${
              product.reverse ? "lg:grid-flow-col-dense" : ""
            }`}
          >
            {/* Product info */}
            <article
              className={`flex flex-col gap-8 ${product.reverse ? "lg:col-start-2" : ""}`}
            >
              {/* Product pill */}
              <span className="inline-flex w-fit items-center rounded-full border border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand))]/5 px-3 py-1 text-xs font-medium brand-text">
                {t(product.pillEn, product.pillZh)}
              </span>

              {/* Product title */}
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1]">
                <span dangerouslySetInnerHTML={renderTitle(product.titleEn, product.titleZh, [
                  { en: 'structured brand insights', zh: '结构化品牌洞察' },
                  { en: 'real feedback', zh: '真实反馈' },
                  { en: 'scalable', zh: '规模化生产' },
                  { en: 'media distribution scenarios', zh: '媒体传播场景' },
                ])} />
              </h2>

              {/* Product description */}
              <p className="max-w-xl text-base leading-relaxed text-muted-foreground">
                {t(product.descEn, product.descZh)}
              </p>

              {/* Product tags */}
              <div className="flex flex-wrap gap-2">
                {product.tagsEn.map((tag, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-[hsl(var(--brand))] bg-[hsl(var(--brand))] px-3 py-1 text-xs text-black"
                  >
                    {t(tag, product.tagsZh[i])}
                  </span>
                ))}
              </div>

              {/* Product caption */}
              {/* <div className="mt-4 max-w-xl border-l-2 border-border pl-4">
                <p className="text-sm leading-relaxed text-muted-foreground/80">
                  {t(product.captionEn, product.captionZh)}
                </p>
              </div> */}
            </article>

            {/* Product image */}
            <figure
              className={`relative ${product.reverse ? "lg:col-start-1" : ""}`}
              aria-hidden="true"
            >
              <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-[hsl(var(--brand))]/10 via-transparent to-transparent blur-2xl" />
              <div className="glass-strong rounded-2xl overflow-hidden nebula-border">
                <img
                  src={product.image}
                  alt=""
                  className="w-full"
                  loading="lazy"
                />
              </div>
            </figure>
          </div>
        </section>
      ))}

      {/* Core Technology Section */}
      <section id="core" className="relative border-t border-border px-6 py-32 bg-background/50">
        <div className="mx-auto max-w-[min(1600px,95vw)]">
          {/* Section header */}
          <header className="mb-20 grid items-end gap-8 lg:grid-cols-2">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] brand-text">
                {t("· Three Core Technology Pillars", "· 三大底层能力中台")}
              </span>
              <h2 className="mt-6 text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1]">
                {t(
                  <>
                    Let <span className="italic">memory, data, and models</span>
                    <br />
                    support the complete product chain.
                  </>,
                  <>
                    让<span className="italic">记忆、数据与模型</span>，
                    <br />
                    支撑完整产品链路。
                  </>,
                )}
              </h2>
            </div>
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground lg:justify-self-end">
              {t(
                "The technology platform consists of a memory platform, data platform, and model platform. It centrally manages brand context, multi-platform data, and model orchestration capabilities, providing continuous and consistent foundational support for all Oran AI product modules.",
                "技术中台由记忆中台、数据中台与模型中台组成，统一管理品牌上下文、多平台数据与模型调度能力，为 OranAI 各产品模块提供持续一致的底层支撑。",
              )}
            </p>
          </header>

          {/* Core cards grid */}
          <div className="grid gap-6 md:grid-cols-3">
            {coreCards.map((card, index) => (
              <article
                key={card.nameEn}
                className="group flex flex-col overflow-hidden rounded-2xl glass nebula-border transition-all hover:shadow-2xl"
              >
                {/* Card header */}
                <div className="border-b border-border p-6">
                  <div className="text-sm text-muted-foreground">
                    {t(`0${index + 1}`, `0${index + 1}`)}
                  </div>
                  <div className="mt-1 text-3xl font-medium brand-text">
                    {card.nameEn}
                  </div>
                </div>

                {/* Card image */}
                <figure className="aspect-[3/2] w-full overflow-hidden" aria-hidden="true">
                  <img
                    src={card.image}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </figure>

                {/* Card content */}
                <div className="border-t border-border p-6">
                  <h3 className="text-xl font-medium">{t(card.nameEn, card.nameZh)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {t(card.descEn, card.descZh)}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Core description */}
          <p className="mx-auto mt-20 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground/70">
            {t(
              "Product capabilities do not operate in isolation. They are jointly driven by unified brand memory, multi-platform data, and model orchestration. The technology platform keeps brand context consistent across the full chain, enables continuous data feedback, and automatically matches the right model capabilities to different tasks.",
              "产品能力并不是孤立运行，而是由统一的品牌记忆、多平台数据与模型调度共同驱动。技术中台负责让品牌上下文在全链路中保持一致，让数据持续回流，并为不同任务自动匹配合适的模型能力。",
            )}
          </p>
        </div>
      </section>

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default ProductsPage;